import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import BrandHeader from '../components/BrandHeader'
import AuthLayout from '../components/AuthLayout'
import { api } from '../../../shared/services/API'

type Status = 'confirming' | 'done' | 'invalid'

// Confirma o email do administrador a partir do link enviado no registo
// (TenantService#confirmarEmail). A conta já está acessível antes disto —
// este ecrã só evita que TenantConfirmacaoScheduler a apague por falta de
// confirmação dentro do prazo. Sem formulário: o próprio carregamento da
// página dispara a confirmação, como um clique já dado no botão do email.
export default function ConfirmEmail() {
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token') ?? ''

    const [status, setStatus] = useState<Status>('confirming')
    const [empresa, setEmpresa] = useState('')

    useEffect(() => {
        if (!token) {
            setStatus('invalid')
            return
        }
        let cancelled = false
        api.post('/api/public/tenants/confirmar-email', { token })
            .then((res) => {
                if (cancelled) return
                setEmpresa(res.data?.empresa ?? '')
                setStatus('done')
            })
            .catch(() => {
                if (!cancelled) setStatus('invalid')
            })
        return () => {
            cancelled = true
        }
    }, [token])

    return (
        <AuthLayout>
            <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: '1px solid #E5E7EB' }}>
                <BrandHeader />

                {status === 'confirming' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">
                            A confirmar o seu email…
                        </Typography>
                    </Box>
                )}

                {status === 'invalid' && (
                    <Box sx={{ py: 1 }}>
                        <Typography variant="h5" sx={{ mb: 1 }}>
                            Link inválido ou expirado
                        </Typography>
                        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                            Este link de confirmação já foi usado, expirou, ou a conta foi removida por falta de
                            confirmação dentro do prazo.
                        </Alert>
                        <Button component={Link} to="/login" variant="contained" size="large" fullWidth>
                            Ir para o login
                        </Button>
                    </Box>
                )}

                {status === 'done' && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Box
                            sx={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                bgcolor: '#EEF2FF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2.5,
                            }}
                        >
                            <CheckCircleOutlinedIcon sx={{ fontSize: 34, color: 'primary.light' }} />
                        </Box>
                        <Typography variant="h5" sx={{ mb: 1 }}>
                            Email confirmado!
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {empresa ? `A conta da empresa ${empresa} está confirmada.` : 'A sua conta está confirmada.'}{' '}
                            Já pode iniciar sessão.
                        </Typography>
                        <Button component={Link} to="/login" variant="contained" size="large" fullWidth>
                            Ir para o login
                        </Button>
                    </Box>
                )}
            </Paper>
        </AuthLayout>
    )
}
